import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import { AlertTriangle, CheckCircle2, Phone, MapPin, Clock } from "lucide-react";

/**
 * "Need Help Now" — dedicated urgent intake for vehicle roadside emergencies
 * and urgent home services (plumber, electrician, locksmith, etc.).
 * Reuses the existing /api/leads endpoint with urgent=true.
 */
export default function EmergencyForm() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { id, ref } | null
  const [form, setForm] = useState({
    category: sp.get("category") || "",
    postcode: sp.get("postcode") || "",
    name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    description: "",
    when_needed: "asap",
    urgent: true,
  });

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const emergencyCats = useMemo(
    () => cats.filter((c) => c.emergency === true),
    [cats]
  );

  const grouped = useMemo(() => {
    const vehicle = emergencyCats.filter((c) => c.group === "vehicle");
    const home = emergencyCats.filter((c) => c.group !== "vehicle");
    return { vehicle, home };
  }, [emergencyCats]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Please choose an emergency service");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", form);
      toast.success("Emergency request received — help is on the way");
      if (user && user.role === "customer" && data?.id) {
        nav(`/my-request/${data.id}`);
        return;
      }
      setDone({ id: data?.id, ref: data?.ref });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send request");
    } finally { setBusy(false); }
  };

  if (done) {
    const signInHref = `/login?redirect=${encodeURIComponent(`/my-request/${done.id || ""}`)}`;
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <Toaster position="top-center" />
        <div className="max-w-md mx-auto px-4 py-16 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 inline-flex items-center justify-center">
            <CheckCircle2 size={40} className="text-red-600" />
          </div>
          <h1 className="font-display text-3xl font-bold mt-4">Emergency request received</h1>
          {done.ref && <div className="mt-1 text-sm text-slate-500">Reference <b>{done.ref}</b></div>}
          <p className="text-slate-600 mt-2">
            We've alerted every verified local pro who can help. Sign in with the email you provided to see quotes come in live — typical response is under 20 minutes.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500">
            <Clock size={14} /> Keep your phone on you
          </div>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to={signInHref} className="btn-accent" data-testid="emergency-view-quotes">Sign in to view quotes</Link>
            <button
              onClick={() => { setDone(null); setForm({ ...form, description: "" }); }}
              className="btn-outline"
              data-testid="emergency-another"
            >
              Send another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />

      {/* Red urgency banner */}
      <div className="bg-red-600 text-white">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <AlertTriangle size={22} />
          <div className="text-sm sm:text-base font-semibold">
            Need Help Now — urgent vehicle, roadside &amp; home emergency requests are dispatched immediately.
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
          Emergency service in <span className="text-red-600">minutes</span>
        </h1>
        <p className="text-slate-600 mt-2">
          Tell us what's happened and where you are. Your request goes to the nearest available verified provider.
        </p>

        <form onSubmit={submit} className="mt-6 card space-y-4 border-red-100">
          <div>
            <label className="label">What do you need urgently?</label>
            <select data-testid="emg-category" value={form.category} onChange={set("category")} className="field" required>
              <option value="">Choose the emergency service…</option>
              {grouped.vehicle.length > 0 && (
                <optgroup label="Vehicle & Roadside">
                  {grouped.vehicle.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </optgroup>
              )}
              {grouped.home.length > 0 && (
                <optgroup label="Home emergencies">
                  {grouped.home.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </optgroup>
              )}
            </select>
          </div>

          <div>
            <label className="label"><MapPin size={14} className="inline mr-1 text-red-600" />Where are you? (postcode or landmark)</label>
            <input data-testid="emg-postcode" required value={form.postcode} onChange={set("postcode")} className="field" placeholder="e.g. L1 8JQ or M6 J24 southbound" />
          </div>

          <div>
            <label className="label">Your name</label>
            <input data-testid="emg-name" required value={form.name} onChange={set("name")} className="field" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label"><Phone size={14} className="inline mr-1 text-red-600" />Best contact number</label>
              <input data-testid="emg-phone" required value={form.phone} onChange={set("phone")} className="field" placeholder="+44 …" />
            </div>
            <div>
              <label className="label">Email</label>
              <input data-testid="emg-email" type="email" required value={form.email} onChange={set("email")} className="field" />
            </div>
          </div>

          <div>
            <label className="label">What's happened?</label>
            <textarea data-testid="emg-description" required rows={3} value={form.description} onChange={set("description")} className="field" placeholder="e.g. Flat tyre on M62 hard shoulder, small child in the car." />
          </div>

          <button data-testid="emg-submit" disabled={busy} className="w-full rounded-full font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 h-12 flex items-center justify-center gap-2 shadow-lg shadow-red-500/25">
            <AlertTriangle size={18} />
            {busy ? "Sending…" : "Request emergency help now"}
          </button>
          <div className="text-xs text-slate-500 text-center">
            For life-threatening emergencies, call <strong>999</strong>. FixNear dispatches verified local providers only.
          </div>
        </form>
      </div>
    </div>
  );
}
